/**
 * Balance Sheet Account Configuration
 *
 * Defines the structure and hierarchy of balance sheet accounts for display.
 * Maps BigQuery account codes to display names and section organization.
 */

import { AccountConfig } from '../types/balanceSheet';

/**
 * ACTIVOS (Assets) accounts - cuenta_cop_1*
 */
export const ACTIVOS_ACCOUNTS: AccountConfig[] = [
  { code: '1', name: 'Activos', indent: 0, section: 'activos' },
  { code: '11', name: 'Fondos disponibles', indent: 1, section: 'activos' },
  { code: '12', name: 'Operaciones Interbancarias', indent: 1, section: 'activos' },
  { code: '13', name: 'Inversiones', indent: 1, section: 'activos' },
  { code: '14', name: 'Cartera de Créditos Neta', indent: 1, section: 'activos' },
  { code: '15', name: 'Deudores por Aceptación', indent: 1, section: 'activos' },
  { code: '16', name: 'Cuentas por Cobrar', indent: 1, section: 'activos' },
  { code: '17', name: 'Bienes Realizados', indent: 1, section: 'activos' },
  { code: '18', name: 'Propiedades y Equipo', indent: 1, section: 'activos' },
  { code: '19', name: 'Otros Activos', indent: 1, section: 'activos' },
];

/**
 * PASIVOS (Liabilities) accounts - cuenta_cop_2*
 */
export const PASIVOS_ACCOUNTS: AccountConfig[] = [
  { code: '2', name: 'Pasivos', indent: 0, section: 'pasivos' },
  { code: '21', name: 'Obligaciones con el público', indent: 1, section: 'pasivos' },
  { code: '2101', name: 'Depósitos a la vista', indent: 2, section: 'pasivos' },
  { code: '2103', name: 'Depósitos a Plazo', indent: 2, section: 'pasivos' },
  { code: '22', name: 'Operaciones Interbancarias', indent: 1, section: 'pasivos' },
  { code: '23', name: 'Obligaciones Inmediatas', indent: 1, section: 'pasivos' },
  { code: '24', name: 'Aceptaciones en Circulación', indent: 1, section: 'pasivos' },
  { code: '25', name: 'Cuentas por pagar', indent: 1, section: 'pasivos' },
  { code: '26', name: 'Obligaciones Financieras', indent: 1, section: 'pasivos' },
  { code: '27', name: 'Valores en Circulación', indent: 1, section: 'pasivos' },
  { code: '28', name: 'Oblig. convertibles', indent: 1, section: 'pasivos' },
  { code: '29', name: 'Otros Pasivos', indent: 1, section: 'pasivos' },
];

/**
 * PATRIMONIO (Equity) accounts - cuenta_cop_3*
 */
export const PATRIMONIO_ACCOUNTS: AccountConfig[] = [
  { code: '3', name: 'Patrimonio', indent: 0, section: 'patrimonio' },
  { code: '31', name: 'Capital social', indent: 1, section: 'patrimonio' },
  { code: '32', name: 'Prima o descuento', indent: 1, section: 'patrimonio' },
  { code: '33', name: 'Reservas', indent: 1, section: 'patrimonio' },
  { code: '34', name: 'Otros aportes patrimoniales', indent: 1, section: 'patrimonio' },
  { code: '35', name: 'Superávit por valuaciones', indent: 1, section: 'patrimonio' },
  { code: '36', name: 'Resultados', indent: 1, section: 'patrimonio' },
];

/**
 * RESULTADOS (Income Statement) accounts
 * Some rows are calculated from other values
 */
export const RESULTADOS_ACCOUNTS: AccountConfig[] = [
  // Income accounts
  { code: '51', name: 'Intereses y Descuentos Ganados', indent: 1, section: 'resultados' },
  { code: '41', name: 'Intereses Causados', indent: 1, section: 'resultados' },
  {
    code: 'margen_neto_intereses',
    name: 'Margen Neto de Intereses',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: '51 - 41',
  },
  { code: '52', name: 'Comisiones Ganadas', indent: 1, section: 'resultados' },
  { code: '54', name: 'Ingresos por Servicios', indent: 1, section: 'resultados' },
  { code: '42', name: 'Comisiones Causadas', indent: 1, section: 'resultados' },
  { code: '53', name: 'Utilidades Financieras', indent: 1, section: 'resultados' },
  { code: '43', name: 'Pérdidas Financieras', indent: 1, section: 'resultados' },
  {
    code: 'margen_bruto_financiero',
    name: 'Margen Bruto Financiero',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'margen_neto_intereses + 52 + 54 - 42 + 53 - 43',
  },
  { code: '44', name: 'Provisiones', indent: 1, section: 'resultados' },
  {
    code: 'margen_neto_financiero',
    name: 'Margen Neto Financiero',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'margen_bruto_financiero - 44',
  },
  { code: '45', name: 'Gastos de Operación', indent: 1, section: 'resultados' },
  {
    code: 'margen_intermediacion',
    name: 'Margen de Intermediación',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'margen_neto_financiero - 45',
  },
  { code: '55', name: 'Otros Ingresos Operacionales', indent: 1, section: 'resultados' },
  { code: '46', name: 'Otras Pérdidas Operacionales', indent: 1, section: 'resultados' },
  {
    code: 'margen_operacional',
    name: 'Margen Operacional',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'margen_intermediacion + 55 - 46',
  },
  { code: '56', name: 'Otros Ingresos', indent: 1, section: 'resultados' },
  { code: '47', name: 'Otros Gastos y Pérdidas', indent: 1, section: 'resultados' },
  {
    code: 'ganancias_antes_impuestos',
    name: 'Ganancias antes impuestos',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'margen_operacional + 56 - 47',
  },
  { code: '48', name: 'Impuestos y Participación', indent: 1, section: 'resultados' },
  {
    code: '3603',
    name: 'Ganancias del ejercicio',
    indent: 0,
    section: 'resultados',
    isCalculated: true,
    formula: 'ganancias_antes_impuestos - 48',
  },
];

/**
 * All accounts combined for easy iteration
 */
export const ALL_ACCOUNTS: AccountConfig[] = [
  ...ACTIVOS_ACCOUNTS,
  ...PASIVOS_ACCOUNTS,
  ...PATRIMONIO_ACCOUNTS,
  ...RESULTADOS_ACCOUNTS,
];

/**
 * Get all raw account codes that need to be fetched from BigQuery
 * (excludes calculated fields)
 */
export const RAW_ACCOUNT_CODES: string[] = ALL_ACCOUNTS
  .filter((acc) => !acc.isCalculated)
  .map((acc) => acc.code);

/**
 * Section display configuration
 */
export const BALANCE_SHEET_SECTIONS = [
  { id: 'activos', title: 'ACTIVOS', accounts: ACTIVOS_ACCOUNTS },
  { id: 'pasivos', title: 'PASIVOS', accounts: PASIVOS_ACCOUNTS },
  { id: 'patrimonio', title: 'PATRIMONIO', accounts: PATRIMONIO_ACCOUNTS },
  { id: 'resultados', title: 'ESTADO DE RESULTADOS', accounts: RESULTADOS_ACCOUNTS },
] as const;

/**
 * Map account code to BigQuery column name
 * BigQuery uses format: cuenta_cop_XX
 */
export function getBalanceSheetItemCode(accountCode: string): string {
  // Calculated fields don't have a BigQuery column
  if (accountCode.startsWith('margen') || accountCode.startsWith('ganancias')) {
    return accountCode;
  }
  return accountCode;
}

/**
 * Convert period string to last day of month date
 * "2024-03" → "2024-03-31"
 */
export function periodToDate(period: string): string {
  const [year, month] = period.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${month.toString().padStart(2, '0')}-${lastDay}`;
}

/**
 * Format period for display
 * "2024-03" → "Mar-24"
 */
export function formatPeriodShort(period: string): string {
  const [year, month] = period.split('-');
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${monthNames[parseInt(month) - 1]}-${year.slice(2)}`;
}

/**
 * Format period for display (December only shows year)
 * "2024-12" → "Dic-24"
 * Used for YoY column headers
 */
export function formatPeriodDecember(period: string): string {
  const [year] = period.split('-');
  return year.slice(2);
}
